'use client';
import React from 'react';
import * as Icon from '@/ui/Icon';
import pluralize from 'pluralize';

import { toTimeAgo } from '@/lib/dateTime';
import emojify from '@/lib/emojify';

import { ICommit, IRepository } from '@/types';

import { IRepositoryCompare } from '@/lib/repositoryCompareApi';
import Avatar from '@/components/Avatar';
import { Stack, Row, Card } from '@/ui/primatives';
import Tag from '@/ui/Tag';

import { CommitDiffView } from '@/components/Repository/DiffView';

import VersionSelect from './VersionSelect';
import { colors } from '@/styles';

const CommitCard: React.FC<{ commit: ICommit; title: string }> = ({ commit, title }) => {
  return (
    <Card compressed style={{ flex: 1, gap: 10, background: colors.primary.white }}>
      <Row gap={5} alignItems="center" justifyContent="space-between">
        <div>{emojify(commit.message)}</div>
        <Tag small>
          <span>{title}:</span> <span className="mono">{commit.id}</span>
        </Tag>
      </Row>
      <Row gap={5} alignItems="center">
        <Avatar name={commit.author} email={commit.email || '?'} size={14} />
        <span style={{ fontWeight: 500 }}>{commit.author || 'unknown'}</span>
        <span>{`committed ${toTimeAgo(commit.timestamp)}`}</span>
      </Row>
    </Card>
  );
};

export const CompareCommitSelector: React.FC<{
  repository: IRepository;
  commits: ICommit[];
  base?: string;
  head?: string;
}> = ({ repository, commits, base, head }) => {
  const [baseCommit, setBaseCommit] = React.useState<ICommit | undefined>();
  const [headCommit, setHeadCommit] = React.useState<ICommit | undefined>();
  return (
    <Stack gap={20} style={{ flex: 1, width: '100%' }}>
      <Card compressed stretch>
        <Row gap={20} alignItems="center">
          <Icon.Compare />
          <div style={{ flex: 1 }}>
            <VersionSelect
              versions={commits}
              selectedVersion={commits.find((c) => c.id === base)}
              placeholder="base"
              onChange={(v) => setBaseCommit(v)}
            />
          </div>
          <Icon.ArrowLeft />
          <div style={{ flex: 1 }}>
            <VersionSelect
              versions={commits}
              selectedVersion={commits.find((c) => c.id === head)}
              placeholder="head"
              onChange={(v) => setHeadCommit(v)}
            />
          </div>
        </Row>
      </Card>
      {baseCommit && headCommit && (
        <>
          <Row gap={20} alignItems="center" justifyContent="space-between">
            <CommitCard title="base" commit={baseCommit} />
            <Icon.ArrowLeft />
            <CommitCard title="head" commit={headCommit} />
          </Row>
          <CommitDiffView repository={repository} baseRef={baseCommit.id} headRef={headCommit.id} />
        </>
      )}
    </Stack>
  );
};

const FileChangeSummaryTags: React.FC<{ compare: IRepositoryCompare }> = ({ compare }) => {
  return (
    <Row gap={20} style={{ display: 'inline-flex' }}>
      <Tag pill small variant="success">
        {pluralize('file', compare.counts.added, true)} added
      </Tag>
      <Tag pill small variant="error">
        {pluralize('file', compare.counts.removed, true)} removed
      </Tag>
      <Tag pill small variant="info">
        {pluralize('file', compare.counts.modified, true)} changed
      </Tag>
    </Row>
  );
};

export const RepositoryCompareSummaryRow: React.FC<{ compare: IRepositoryCompare; children?: React.ReactNode }> = ({
  compare,
  children,
}) => {
  return children ? (
    <Row gap={50} alignItems="center" justifyContent="space-between">
      <FileChangeSummaryTags compare={compare} />
      {children}
    </Row>
  ) : (
    <FileChangeSummaryTags compare={compare} />
  );
};
